<template>
  <div id="app" :class="{ 'app--mobile-shell': isMobileShellRoute }">
    <div
      v-if="showNetworkBanner || outboundPending > 0"
      class="network-banner"
      role="status"
    >
      <div v-if="showNetworkBanner">network connection is not reliable.</div>
      <div v-if="outboundPending > 0">{{ outboundHoldText }}</div>
    </div>
    <NavBar v-if="shouldShowNavBar" />
    <main class="app-view" :class="{ 'app-view--mobile': isMobileShellRoute }">
      <div v-if="routeLoading" class="route-loading-shell">
        <div class="route-loading-card">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Loading…</span>
        </div>
      </div>
      <!--
        Wrap router-view so v-show is on a real element. DocumentScan (and
        other pages) have multi-root templates; Vue cannot apply directives
        directly on fragment roots.
      -->
      <div v-show="!routeLoading" class="route-view-host">
        <router-view />
      </div>
    </main>

    <!-- Mini Voice Memo Player -->
    <MiniVoiceMemoPlayer ref="miniVoiceMemoPlayer" />

    <!-- Global error popup, shown when an error occurs (DISABLED)
    <div v-if="errorMessage" style="padding-top: 10px">
      <div class="error-popup">
        <p>
          Well, isn't that embarrassing! Something went splat.
          <i class="fas fa-bug"></i>
        </p>
        <p v-html="errorMessage"></p>
        <pre
          v-if="errorStack"
          @click="selectErrorText"
          title="Click to select all"
          >{{ errorStack }}</pre
        >
        <button @click="closeError" class="error-close-btn">Close</button>
      </div>
    </div>
    -->

    <!-- Service unavailable popup -->
    <div
      v-if="serviceUnavailable"
      class="error-popup"
      style="padding-top: 10px"
    >
      <p>The service is temporarily unavailable. Please try again soon.</p>
      <button @click="closeServiceUnavailable">Close</button>
    </div>

    <!-- Small Vue error notification -->
    <div v-if="showVueErrorNotification" class="vue-error-notification">
      <p>
        This page had an error. It has been reported. Please try again or wait a
        bit.
      </p>
      <button @click="dismissVueErrorNotification">Dismiss</button>
    </div>
  </div>
</template>

<script>
// Import the NavBar component
import NavBar from '@/components/NavBar.vue';
import MiniVoiceMemoPlayer from '@/components/MiniVoiceMemoPlayer.vue';
import axios from '@/axiosConfig';
import { getCurrentInstance } from 'vue';
import { applyPageScrollFromLocation } from '@/utils/pageScroll';
import { isPwaUpdateCooldown } from '@/utils/pwaUpdate';
import { isMobileDevice } from '@/utils/deviceHome';
import {
  NETWORK_CHECK_MS,
  isMobileShellPath,
  showsNetworkBannerOnRoute,
  readNavigatorOnline,
  emptyNetworkBannerState,
  stepNetworkBanner,
} from '@/utils/networkBanner';
import { uploadFileLikeWorkOrder } from '@/utils/chunkedHeicUpload';
import {
  OUTBOUND_HOLD_TEXT,
  installOutboundQueue,
} from '@/utils/outboundQueue';

export default {
  name: 'App',
  components: {
    NavBar,
    MiniVoiceMemoPlayer,
  },
  data() {
    return {
      errorMessage: null, // Store the global error message here
      errorStack: null, // Store the stack trace here
      serviceUnavailable: false, // Flag to show service unavailable popup
      showVueErrorNotification: false, // Flag to show the small Vue error notification
      vueCompilerErrorHandler: null, // Reference to webpack error handler
      serviceCheckInterval: null, // Interval for checking service availability
      // True while router beforeEach awaits auth — avoids blank grey shell on mobile
      routeLoading: true,
      networkBannerVisible: false,
      outboundPending: 0,
    };
  },

  computed: {
    hiddenNavRoutes() {
      return [
        '/',
        '/login',
        '/mnotes',
        '/mdocs',
        '/home',
        '/mhome',
        '/ichat',
        '/nothing',
        '/quicklook',
        '/mtenantsearch',
        '/mworkorders',
        '/msms',
        '/mvoice',
        '/lockcodes',
        '/appliances',
        '/quickemail',
        '/download',
        '/mproperties',
        '/collector',
        '/survey',
        '/calendar',
        '/workorder-schedule',
        '/rent-me',
      ];
    },
    shouldShowNavBar() {
      if (this.$route.query.hideNav === '1') return false;
      return !this.hiddenNavRoutes.includes(this.$route.path);
    },
    // Mobile-first routes only: phone-width column centered on desktop.
    // Do not key off hiddenNavRoutes — /home and /login hide the nav but are
    // full-width desktop layouts (/mhome is the mobile home).
    isMobileShellRoute() {
      return isMobileShellPath(this.$route.path);
    },
    showNetworkBanner() {
      return (
        this.networkBannerVisible &&
        showsNetworkBannerOnRoute(this.$route.path, isMobileDevice())
      );
    },
    outboundHoldText() {
      return OUTBOUND_HOLD_TEXT;
    },
  },

  methods: {
    // Select all text in the error stack trace
    selectErrorText(event) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(event.target);
      selection.removeAllRanges();
      selection.addRange(range);
    },

    // Close the error popup
    closeError() {
      this.errorMessage = null;
      this.errorStack = null; // Clear the stack trace
    },

    // Close the service unavailable popup
    closeServiceUnavailable() {
      this.serviceUnavailable = false;
      this.stopServiceCheck(); // Stop checking when manually closed
    },

    // Start checking for service availability at regular intervals
    startServiceCheck() {
      // Clear any existing interval to avoid duplicates
      this.stopServiceCheck();

      // Set up new interval to check every N seconds
      this.serviceCheckInterval = setInterval(async () => {
        try {
          // Try to reach the service
          await axios.get('/api/service-check');

          // If successful, clear error and service unavailable flags
          this.errorMessage = null;
          this.serviceUnavailable = false;

          // Stop checking since service is now available
          this.stopServiceCheck();
          console.log('Service is now available, automatic recovery complete');
        } catch (error) {
          // Service still unavailable, continue checking
          console.log(
            'Service still unavailable, will check again in 30 seconds',
          );
        }
      }, 10000); // Check every 10 seconds

      console.log('Started automatic service availability check');
    },

    // Stop checking for service availability
    stopServiceCheck() {
      if (this.serviceCheckInterval) {
        clearInterval(this.serviceCheckInterval);
        this.serviceCheckInterval = null;
      }
    },

    // Dismiss the Vue error notification
    dismissVueErrorNotification() {
      this.showVueErrorNotification = false;
    },

    async syncMobileOrientationLock(shouldLock) {
      const orientation = screen.orientation;
      if (!orientation) return;
      try {
        if (shouldLock && typeof orientation.lock === 'function') {
          await orientation.lock('portrait');
        } else if (!shouldLock && typeof orientation.unlock === 'function') {
          orientation.unlock();
        }
      } catch (_) {
        // Lock only works in an installed PWA / fullscreen on many browsers.
      }
    },

    // Auto-close Vue error notification after 10 seconds
    setAutoCloseVueErrorNotification() {
      if (this.showVueErrorNotification) {
        setTimeout(() => {
          this.showVueErrorNotification = false;
        }, 10000); // 10 seconds
      }
    },

    // Handle global errors
    async handleGlobalError(event) {
      const error = event.detail;

      if (isPwaUpdateCooldown()) {
        return;
      }

      // Skip showing the error popup if the error is related to service-check
      if (
        error.response &&
        error.response.config &&
        error.response.config.url &&
        error.response.config.url.includes('/api/service-check')
      ) {
        // Still log the error to chat if needed, but don't show the popup
        this.logErrorToChat(error);
        // Set service unavailable flag if appropriate
        if (error.response.status >= 500) {
          this.serviceUnavailable = true;
          this.startServiceCheck(); // Start checking for service recovery
        }
        return;
      }

      // Check if service is available
      try {
        await axios.get('/api/service-check');

        // If the service check passed, show the normal error popup
        if (error.response) {
          this.errorMessage = `<h3>API Error ${error.response.status}: ${error.response.statusText || 'Something went wrong.'}</h3>URL: ${error.response.config.url}`;
          this.errorStack = error.stack || 'No stack trace available';
        } else {
          this.errorMessage = `<h3>API Error</h3> <p>${error.message || 'An unknown error occurred.'}</p>`;
          this.errorStack = error.stack || 'No stack trace available';
        }

        // Log error to chat system
        this.logErrorToChat(error);
      } catch (serviceCheckError) {
        // If service check failed, show the service unavailable popup
        this.serviceUnavailable = true;
        this.errorMessage = null;
        this.errorStack = null;

        // Start checking for service recovery
        this.startServiceCheck();
      }
    },

    // Handle Vue errors and send to chat system
    handleVueError(err, vm, info) {
      console.error('Vue Error:', err);

      // Show the user-friendly notification instead of the full error popup
      // this.showVueErrorNotification = true;

      // Automatically hide the notification after 10 seconds
      setTimeout(() => {
        this.showVueErrorNotification = false;
      }, 10000);

      // Construct error message for logging (not showing to user)
      const errorInfo = {
        message: err.message || 'Unknown Vue error',
        stack: err.stack || 'No stack trace available',
        component: vm?.$options?.name || 'Unknown component',
        info: info || 'No additional info',
        url: window.location.href,
        timestamp: new Date().toISOString(),
      };

      // Send to dedicated error endpoint
      this.logClientErrorToAPI(errorInfo, 'Vue Runtime Error');

      // Don't show in error popup, just log to chat
      this.logErrorToChat(errorInfo);
    },

    // Handle window errors
    handleWindowError(event) {
      if (!event.detail) {
        // Avoid duplicate handling of errors already caught
        const error = {
          message: event.message || 'Unhandled window error',
          stack: event.error ? event.error.stack : 'No stack available',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        };

        this.errorMessage = `<h3>JavaScript Error</h3><p>${error.message}</p><p>File: ${error.filename}:${error.lineno}:${error.colno}</p>`;
        this.errorStack = error.stack;

        this.logErrorToChat(error);
      }

      // Prevent default browser error handling
      event.preventDefault();
      return true;
    },

    // Handle unhandled promise rejections
    handlePromiseRejection(event) {
      const error = {
        message: event.reason?.message || 'Unhandled Promise rejection',
        stack: event.reason?.stack || 'No stack available',
        reason: event.reason,
      };

      this.errorMessage = `<h3>Promise Error</h3><p>${error.message}</p>`;
      this.errorStack = error.stack;

      this.logErrorToChat(error);

      // Prevent default browser error handling
      event.preventDefault();
    },

    // Log error to chat system using chat-messages API
    // DISABLED: error logging to System chat (March 2026)
    // async logErrorToChat(error) {
    //   try {
    //     const routeName = this.$route
    //       ? this.$route.name || this.$route.path
    //       : 'Unknown route';
    //     const message = {
    //       fromUserId: 0,
    //       toUserId: 0,
    //       message: JSON.stringify({
    //         type: 'error',
    //         title: 'Client-side Error',
    //         route: routeName,
    //         url: window.location.href,
    //         message: error.message || (error.response ? `API Error ${error.response.status}` : 'Unknown error'),
    //         stack: error.stack || 'No stack trace available',
    //         timestamp: new Date().toISOString(),
    //       }),
    //       isSystemMessage: true,
    //     };
    //     await axios.post('/api/chat-messages', message);
    //   } catch (chatError) {
    //     console.error('Failed to log error to chat:', chatError);
    //   }
    // },
    logErrorToChat() {
      /* disabled */
    },

    // Log error to the new dedicated endpoint
    // DISABLED: error logging to System chat (March 2026)
    // async logClientErrorToAPI(error, errorType = 'Vue Compiler Error') {
    //   try {
    //     const errorInfo = {
    //       errorType,
    //       message: error.message || 'Unknown error',
    //       stack: error.stack || 'No stack trace available',
    //       componentName: error.component || error.vm?.$options?.name || 'Unknown component',
    //       url: window.location.href,
    //       code: error.code || '',
    //     };
    //     await axios.post('/api/log-client-error', errorInfo);
    //   } catch (apiError) {
    //     console.error('Failed to log error to API:', apiError);
    //   }
    // },
    logClientErrorToAPI() {
      /* disabled */
    },

    // New method to handle build-time errors from webpack
    registerBuildTimeErrorHandler() {
      // Check if we're in development mode and have webpack hot module replacement
      if (
        window.__webpack_hot_middleware_reporter__ ||
        window.webpackHotUpdate
      ) {
        // Access webpack's error overlay system if available
        const originalErrorHandler = window.onerror;

        window.onerror = (message, source, lineno, colno, error) => {
          // Check if error is from webpack and contains "Module Error" or "Compiled with problems"
          if (
            source &&
            (source.includes('webpack-dev-server') ||
              message.includes('Module Error') ||
              message.includes('Compiled with problems'))
          ) {
            // Directly log to dedicated API endpoint
            this.logClientErrorToAPI(
              {
                message: `Build Error: ${message}`,
                stack: `Source: ${source}, Line: ${lineno}, Column: ${colno}\n${error ? error.stack : ''}`,
                code: message,
              },
              'Webpack Build Error',
            );

            // Show user-friendly notification
            // this.showVueErrorNotification = true;

            // Automatically hide the notification after 10 seconds
            setTimeout(() => {
              this.showVueErrorNotification = false;
            }, 10000);
          }

          // Call original handler if it exists
          if (originalErrorHandler) {
            return originalErrorHandler(message, source, lineno, colno, error);
          }

          return false;
        };

        // Intercept console.error calls that might contain webpack errors
        const originalConsoleError = console.error;
        this.vueCompilerErrorHandler = (...args) => {
          const errorString = args.join(' ');

          // Check for common Vue compiler error patterns
          if (
            errorString.includes('Module Error') ||
            errorString.includes('Compiled with problems') ||
            errorString.includes('Module build failed') ||
            errorString.includes('VueCompilerError') ||
            errorString.includes('Element is missing end tag') ||
            errorString.includes('Parsing error') ||
            errorString.includes('Unexpected closing tag')
          ) {
            // Directly log to API
            this.logClientErrorToAPI(
              {
                message: 'Vue Compiler Error',
                stack: errorString,
                code: errorString,
              },
              'Vue Compiler Error',
            );

            // Show user-friendly notification
            // this.showVueErrorNotification = true;

            // Automatically hide the notification after 10 seconds
            setTimeout(() => {
              this.showVueErrorNotification = false;
            }, 10000);
          }

          // Always call original console.error
          originalConsoleError.apply(console, args);
        };

        // Replace console.error with our custom handler
        console.error = this.vueCompilerErrorHandler;
      }

      // Attempt to hook into webpack's error overlay directly if available
      if (window.__webpack_error_overlay__) {
        const originalHandleRuntimeError =
          window.__webpack_error_overlay__.handleRuntimeError;
        window.__webpack_error_overlay__.handleRuntimeError = (error) => {
          // Show our notification
          // this.showVueErrorNotification = true;

          // Log directly to API
          this.logClientErrorToAPI(
            {
              message: 'Webpack Runtime Error',
              stack: error.stack || error.message,
            },
            'Webpack Runtime Error',
          );

          // Call original handler
          return originalHandleRuntimeError(error);
        };
      }

      // Add a global MutationObserver to detect webpack error overlays being added to the DOM
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === 1) {
                // Element node
                // Check for webpack error overlay elements
                if (
                  node.classList?.contains('error-overlay') ||
                  node.id?.includes('webpack-dev-server-client-overlay') ||
                  node.innerHTML?.includes('Module Error') ||
                  node.innerHTML?.includes('Element is missing end tag') ||
                  node.innerHTML?.includes('Unexpected closing tag')
                ) {
                  // Log the content of the error overlay
                  this.logClientErrorToAPI(
                    {
                      message: 'DOM Error Overlay Detected',
                      stack:
                        node.textContent ||
                        node.innerHTML ||
                        'No content extracted',
                      code: node.innerHTML,
                    },
                    'Webpack Error Overlay',
                  );

                  // Show our notification
                  // this.showVueErrorNotification = true;
                }
              }
            }
          }
        }
      });

      // Start observing the document body for added nodes
      observer.observe(document.body, { childList: true, subtree: true });
    },

    // Disable double-tap zoom functionality
    disableDoubleTapZoom() {
      let lastTouchEnd = 0;
      document.addEventListener(
        'touchend',
        (e) => {
          const now = new Date().getTime();
          if (now - lastTouchEnd <= 300) {
            e.preventDefault();
          }
          lastTouchEnd = now;
        },
        false,
      );
    },

    flushOutboundQueue() {
      if (this._flushOutbound) this._flushOutbound();
    },

    startNetworkWatch() {
      this.stopNetworkWatch();
      this._networkState = emptyNetworkBannerState();
      const sample = () => {
        const online = readNavigatorOnline();
        const hadOutage = !!(
          this._networkState && this._networkState.offlineSince
        );
        this._networkState = stepNetworkBanner(
          this._networkState,
          online,
          Date.now(),
        );
        this.networkBannerVisible = this._networkState.visible;
        if (online && (hadOutage || this.outboundPending > 0)) {
          this.flushOutboundQueue();
        }
      };
      sample();
      this._networkTimer = setInterval(sample, NETWORK_CHECK_MS);
      this._onNetworkChange = sample;
      window.addEventListener('online', this._onNetworkChange);
      window.addEventListener('offline', this._onNetworkChange);
    },

    stopNetworkWatch() {
      if (this._networkTimer) {
        clearInterval(this._networkTimer);
        this._networkTimer = null;
      }
      if (this._onNetworkChange) {
        window.removeEventListener('online', this._onNetworkChange);
        window.removeEventListener('offline', this._onNetworkChange);
        this._onNetworkChange = null;
      }
    },

    // Add meta viewport tag to head to disable scaling
    addViewportMetaTag() {
      // Remove existing viewport meta if present
      const existingMeta = document.querySelector('meta[name="viewport"]');
      if (existingMeta) {
        existingMeta.remove();
      }

      // Create and add new meta tag with scaling restrictions
      const metaTag = document.createElement('meta');
      metaTag.name = 'viewport';
      metaTag.content =
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(metaTag);
    },
  },

  watch: {
    '$route.query.scroll'() {
      applyPageScrollFromLocation();
    },
    isMobileShellRoute: {
      immediate: true,
      handler(shouldLock) {
        this.syncMobileOrientationLock(shouldLock);
      },
    },
  },

  created() {
    applyPageScrollFromLocation();
    this._onRouteLoading = (event) => {
      this.routeLoading = !!(event?.detail && event.detail.isLoading);
    };
    window.addEventListener('pixie-route-loading', this._onRouteLoading);
  },

  mounted() {
    this.syncMobileOrientationLock(this.isMobileShellRoute);

    // Listen for global error events from Axios
    window.addEventListener('global-error', this.handleGlobalError);

    // Set up global Vue error handler for Vue 3
    // We need to use getCurrentInstance() to access app instance in Vue 3
    const app = getCurrentInstance();
    if (app) {
      app.appContext.app.config.errorHandler = (err, vm, info) => {
        this.handleVueError(err, vm, info);
      };
    }

    // Add window unhandled error handler
    window.addEventListener('error', this.handleWindowError);

    // Add unhandled promise rejection handler
    window.addEventListener('unhandledrejection', this.handlePromiseRejection);

    // Register build-time error handler
    this.registerBuildTimeErrorHandler();

    // Add mobile pan/zoom prevention
    this.addViewportMetaTag();
    this.disableDoubleTapZoom();
    this.startNetworkWatch();
    const outbound = installOutboundQueue({
      post: (url, body, config) => axios.post(url, body, config),
      put: (url, body, config) => axios.put(url, body, config),
      uploadFile: uploadFileLikeWorkOrder,
      isOnline: () => readNavigatorOnline(),
      onChange: (count) => {
        this.outboundPending = count;
      },
    });
    this._stopOutbound = outbound.stop;
    this._flushOutbound = outbound.flush;

    // Add a global error event listener for webpack chunk loading errors
    window.addEventListener(
      'error',
      (event) => {
        // Check if the error is related to chunk loading or webpack
        if (
          event.target &&
          (event.target.src || event.target.href) &&
          (event.target.src?.includes('chunk-') ||
            event.target.href?.includes('chunk-') ||
            event.target.src?.includes('webpack') ||
            event.target.href?.includes('webpack'))
        ) {
          // This is likely a webpack chunk loading error
          this.logClientErrorToAPI(
            {
              message: `Chunk loading error: ${event.target.src || event.target.href}`,
              stack: `Failed to load resource: ${event.target.nodeName}: ${
                event.target.src || event.target.href
              }`,
            },
            'Webpack Chunk Error',
          );

          // Show user-friendly notification
          // this.showVueErrorNotification = true;
        }
      },
      true,
    ); // Capture phase to catch errors before they propagate

    // Hook into webpack's hot module replacement events
    if (module && module.hot) {
      module.hot.addStatusHandler((status) => {
        if (status === 'abort' || status === 'fail') {
          // When webpack build fails, show our notification
          // this.showVueErrorNotification = true;

          // Log the error
          this.logClientErrorToAPI(
            {
              message: `Webpack build status: ${status}`,
              stack: `HMR build failed with status: ${status}`,
            },
            'Webpack HMR Failure',
          );
        }
      });
    }
  },

  beforeUnmount() {
    // Remove event listeners to avoid memory leaks
    window.removeEventListener('global-error', this.handleGlobalError);
    window.removeEventListener('error', this.handleWindowError);
    window.removeEventListener(
      'unhandledrejection',
      this.handlePromiseRejection,
    );
    if (this._onRouteLoading) {
      window.removeEventListener('pixie-route-loading', this._onRouteLoading);
    }
    if (this._stopOutbound) this._stopOutbound();
    this._flushOutbound = null;
    this.stopNetworkWatch();

    // Restore original console.error if we replaced it
    if (this.vueCompilerErrorHandler) {
      const originalConsoleError = console.error;
      if (console.error === this.vueCompilerErrorHandler) {
        console.error = originalConsoleError;
      }
    }
  },
};
</script>

<style>
/* Global styles to prevent pan/zoom on mobile devices */
html,
body {
  height: 100%;
  margin: 0;
  overflow: hidden !important;
  touch-action: none;
  -webkit-overflow-scrolling: none;
  overscroll-behavior: none;
  position: fixed;
  width: 100%;
}

/* Prevent iOS-specific behaviors */
* {
  -webkit-touch-callout: none; /* Disable callout */
  -webkit-user-select: none; /* Disable selection */
  user-select: none; /* Standard property for compatibility */
  -webkit-tap-highlight-color: transparent; /* Remove tap highlight */
}

/* Allow scrollable areas within the app */
.scrollable {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  height: 100%;
}

/*
 * Opt-in document scroll for Grok Bot / automation.
 * Off by default. Enable with ?scroll=1 or the NavBar Welcome menu.
 */
html.pixie-page-scroll,
html.pixie-page-scroll body {
  overflow: auto !important;
  position: static !important;
  height: auto !important;
  max-height: none !important;
  width: auto !important;
  min-height: 100%;
  touch-action: auto !important;
  overscroll-behavior: auto !important;
  -webkit-overflow-scrolling: touch;
}

html.pixie-page-scroll #app,
html.pixie-page-scroll .app-view,
html.pixie-page-scroll .route-view-host {
  overflow: visible !important;
  height: auto !important;
  max-height: none !important;
  min-height: 100vh;
}
</style>

<style scoped>
#app {
  display: flex;
  flex-direction: column; /* Makes children stack vertically */
  height: 100vh; /* Ensures the app takes full viewport height */
  overflow: hidden;
}

#app.app--mobile-shell {
  background: #6c6b6b91;
  align-items: center;
}

.network-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100000;
  box-sizing: border-box;
  background: #fff4b0;
  color: #000;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  padding: calc(14px + env(safe-area-inset-top, 0px)) 12px 14px;
  pointer-events: none;
}

.app-view {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.route-view-host {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.app-view--mobile {
  max-width: 440px;
  background: #eee;
  box-shadow: 0 0 28px rgba(0, 0, 0, 0.18);
  /* Containing block for page overlays (Review, dialogs). Without this,
     position:absolute children bind to body and sit at the left of the
     viewport instead of covering this centered column on desktop. */
  position: relative;
}

.route-loading-shell {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  width: 100%;
  background: #eee;
}

.route-loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #675769;
  font-size: 1rem;
  font-weight: 600;
}

.route-loading-card i {
  font-size: 1.6rem;
}

.main-content {
  flex: 1; /* Allows the content to take up the remaining space */
  overflow: auto; /* Ensures content can scroll if it overflows */
}

html,
body {
  height: 100%;
  margin: 0;
  /* display: flex; */
  justify-content: center;
  align-items: center;
}

body {
  font-family: 'Arial', sans-serif;
}

body h3 {
  margin-top: 0 !important;
}

.error-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #ffffff;
  color: #373535;
  padding: 20px;
  padding-top: 0;
  border-radius: 5px;
  border: 4pt solid red;
  z-index: 99999;
  width: 350px;
  text-align: left;
  height: 350px;
  overflow-y: hidden; /* Remove scrollbar on error-popup */
  font-size: 0.9em;
  box-shadow: 7px 7px 8px 4px rgba(0, 0, 0, 0.3);
}

.error-popup p {
  margin: 0;
  word-wrap: break-word; /* Ensure long text in the error message wraps properly */
  margin-top: 5px;
}

.error-popup pre {
  background-color: #fff;
  color: #333;
  padding: 10px;
  border-radius: 5px;
  border: 1pt solid #ddd;
  white-space: pre-wrap; /* Preserve whitespace but wrap */
  word-wrap: break-word; /* Allow long words to be broken */
  word-break: break-all; /* Force breaking of all words */
  overflow-wrap: break-word; /* Alternative to word-wrap for better browser support */
  max-height: 200px;
  overflow-y: scroll; /* Add scroll capability */
  scrollbar-width: none; /* Hide scrollbar in Firefox */
  -ms-overflow-style: none; /* Hide scrollbar in IE/Edge */
  font-size: 0.85em;
  cursor: pointer; /* Indicate clickability */
  user-select: text; /* Ensure text is selectable */
}

.error-popup pre:hover {
  background-color: #f5f5f5; /* Slight highlight on hover */
}

.error-popup pre::-webkit-scrollbar {
  display: none; /* Hide scrollbar in Chrome/Safari/Webkit */
}

.error-popup button {
  background-color: #fd0000;
  color: #fffbfb;
  border: none;
  padding: 10px;
  border-radius: 5px;
  border: 1pt solid #ccc;
  cursor: pointer;
  margin-top: 10px;
  position: absolute;
  bottom: 5px;
  right: 5px;
  z-index: 2; /* Ensure button stays above other elements */
}

.error-popup button:hover {
  background-color: #f0f0f0;
}

.vue-error-notification {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 10px 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  max-width: 300px;
  font-size: 14px;
  animation: fadeIn 0.3s;
}

.vue-error-notification p {
  margin: 0 0 10px 0;
}

.vue-error-notification button {
  background-color: #721c24;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
}

.vue-error-notification button:hover {
  background-color: #5a171d;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

h3 {
  margin-top: 0 !important;
}
</style>
